/**
 * Archive API — Spec 159: Dynamic Evolution Archive
 */

import api from './client';

export type ArchiveCategory =
  | 'tasks'
  | 'contracts'
  | 'limits'
  | 'scenes'
  | 'journals'
  | 'training'
  | 'chastity'
  | 'praise'
  | 'health'
  | 'reviews';

export interface ArchiveTimelineEvent {
  id: string;
  date: string;
  category: ArchiveCategory;
  title: string;
  summary: string;
  detail: Record<string, unknown>;
  icon: string;
}

export interface InsightStat {
  value: number;
  change: number;
  changePercent: number;
  direction: 'up' | 'down' | 'stable';
}

export interface ArchiveMilestone {
  type: string;
  label: string;
  date: string;
}

export interface ArchiveInsights {
  tasksCompleted: InsightStat;
  scenesLogged: InsightStat;
  limitsNegotiated: { value: number; expanded: number; newBoundary: number };
  avgSceneRating: InsightStat;
  journalEntries: InsightStat;
  moodTrend: { current: number; previous: number; direction: string };
  milestones: ArchiveMilestone[];
}

export interface ArchiveTimelineResponse {
  period: { start: string; end: string };
  events: ArchiveTimelineEvent[];
  pagination: { page: number; totalPages: number; totalEvents: number };
  insights: ArchiveInsights;
}

export interface ArchivePdfJobResponse {
  jobId: string;
  status: string;
  estimatedSeconds: number;
}

export interface ArchivePdfStatusResponse {
  status: string;
  downloadUrl?: string;
  error?: string;
}

export const archiveAPI = {
  getTimeline: (
    orgId: string,
    params: {
      start: string;
      end: string;
      categories?: ArchiveCategory[];
      page?: number;
      sort?: 'newest' | 'oldest';
    }
  ) =>
    api.get<{ data: ArchiveTimelineResponse }>(
      `/organizations/${orgId}/archive`,
      {
        params: {
          start: params.start,
          end: params.end,
          categories: params.categories?.join(','),
          page: params.page,
          sort: params.sort,
        },
      }
    ),

  getInsights: (
    orgId: string,
    params: {
      start: string;
      end: string;
      categories?: ArchiveCategory[];
    }
  ) =>
    api.get<{ data: ArchiveInsights }>(
      `/organizations/${orgId}/archive/insights`,
      {
        params: {
          start: params.start,
          end: params.end,
          categories: params.categories?.join(','),
        },
      }
    ),

  exportPdf: (
    orgId: string,
    body: {
      start: string;
      end: string;
      categories?: ArchiveCategory[];
      includeInsights?: boolean;
    }
  ) =>
    api.post<{ data: ArchivePdfJobResponse }>(
      `/organizations/${orgId}/archive/export/pdf`,
      body
    ),

  getPdfStatus: (orgId: string, jobId: string) =>
    api.get<{ data: ArchivePdfStatusResponse }>(
      `/organizations/${orgId}/archive/export/pdf/${jobId}`
    ),

  getCsvUrl: (
    orgId: string,
    params: {
      start: string;
      end: string;
      categories?: ArchiveCategory[];
    }
  ) => {
    const query = new URLSearchParams({
      start: params.start,
      end: params.end,
      ...(params.categories?.length ? { categories: params.categories.join(',') } : {}),
    });
    return `/api/organizations/${orgId}/archive/export/csv?${query.toString()}`;
  },

  getLeaveCsvUrl: (orgId: string) =>
    `/api/organizations/${orgId}/archive/export/leave-csv`,
};
